import { useState, useEffect } from 'react';
import * as Styled from './FileAttachmentsStyles';
import * as FileIcons from '../../../images/file-icons';
import GenericFileIcon from '../../../images/file-icon.svg';
import useBreakpoint from '../../hooks/useBreakpoint';
import mime from 'mime';

export const fileMimeTypes = [
  'image',
  'audio',
  'video',
  'application/pdf',
  'application/msword',
  'application/vnd.ms-word',
  'application/vnd.oasis.opendocument.text',
  'application/vnd.openxmlformats-officedocument.wordprocessingml',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml',
  'application/vnd.oasis.opendocument.spreadsheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml',
  'application/vnd.oasis.opendocument.presentation',
  'application/json',
  'application/gzip',
  'application/zip',
];

export const mimeMapping:any = {
  'image': (<img src={FileIcons.ImageIcon} alt="file icon" />),
  'audio': (<img src={FileIcons.VideoIcon} alt="file icon" />),
  'video': (<img src={FileIcons.VideoIcon} alt="file icon" />),
  'folder': (<img src={FileIcons.FolderIcon} width="24" alt="file icon" />),
  'application/pdf': (<img src={FileIcons.PdfIcon} alt="file icon" />),
  'application/msword': (<img src={FileIcons.WordIcon} alt="file icon" />),
  'application/msonenote': (<img src={FileIcons.OneNoteIcon} alt="file icon" width="24" />),
  'application/onenote': (<img src={FileIcons.OneNoteIcon} alt="file icon" width="24" />),
  'oneNote': (<img src={FileIcons.OneNoteIcon} alt="file icon" width="24" />),
  'application/x-tika-ooxml': (<img src={FileIcons.VisioIcon} alt="file icon" width="24" />),
  'application/vnd.ms-word': (<img src={FileIcons.WordIcon} alt="file icon" />),
  'application/vnd.oasis.opendocument.text': (<img src={FileIcons.WordIcon} alt="file icon" />),
  'application/vnd.openxmlformats-officedocument.wordprocessingml': (<img src={FileIcons.WordIcon} alt="file icon" />),
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': (<img src={FileIcons.WordIcon} alt="file icon" />),
  'application/vnd.ms-excel': (<img src={FileIcons.ExcelIcon} alt="file icon" />),
  'application/vnd.openxmlformats-officedocument.spreadsheetml': (<img src={FileIcons.ExcelIcon} alt="file icon" />),
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': (<img src={FileIcons.ExcelIcon} alt="file icon" />),
  'application/vnd.oasis.opendocument.spreadsheet': (<img src={FileIcons.ExcelIcon} alt="file icon" />),
  'application/vnd.ms-powerpoint': (<img src={FileIcons.ExcelIcon} alt="file icon" />),
  'application/vnd.openxmlformats-officedocument.presentationml': (<img src={FileIcons.PptxIcon} alt="file icon" />),
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': (<img src={FileIcons.PptxIcon} alt="file icon" />),
  'application/vnd.oasis.opendocument.presentation': (<img src={FileIcons.PptxIcon} alt="file icon" />),
  'application/json': (<img src={GenericFileIcon} width="24" alt="file icon" />),
  'application/gzip': (<img src={GenericFileIcon} width="24" alt="file icon" />),
  'application/zip': (<img src={GenericFileIcon} width="24" alt="file icon" />),
  'gdrive/document': (<img src={FileIcons.GDriveDocIcon} width="24" alt="file icon" />),
  'gdrive/spreadsheets': (<img src={FileIcons.GDriveSheetIcon} width="24" alt="file icon" />),
  'gdrive/presentation': (<img src={FileIcons.GDriveSlideIcon} width="24" alt="file icon" />),
}

const getFileIcon = (file:any) => {
  let mimeType = file.mimeType || '';
  if (!mimeType && file.file?.mimeType) {
    mimeType = file.file.mimeType;
  }
  if (file.type === 'folder') {
    return <img src={FileIcons.FolderIcon} width="24" alt="file icon" />
  }
  if (!mimeType) {
    return <img src={GenericFileIcon} width="24" alt="file icon" />
  }
  if (mimeType === 'application/octet-stream') {
    // octet stream can have multiple icons based on extension
    mimeType = mime.getType(file.filename);
    if (!mimeType && file.pl === 'office-365') {
      mimeType = 'oneNote';
    }
  }
  const first = mimeType.split('/')[0];
  if (mimeMapping[first]) {
    return mimeMapping[first]
  }

  if (mimeMapping[mimeType]) {
    return mimeMapping[mimeType]
  }
  return <img src={GenericFileIcon} alt={`file icon ${file.filename}`} width="24" />
}

const filesPerScreenSize:IFilesPerScreenSize = {
  xs: 2,
  sm: 4,
  md: 6,
}

interface IFilesPerScreenSize {
  [key:string]: number,
}

const Attachment = ({ file }: any) => {
  const onAttClick = (e:any) => {
    if (file.url) {
      e.stopPropagation();
    }
    return true;
  }
  return (
    <Styled.FileContainer onClick={onAttClick} href={file.url} target="_blank" >
      <Styled.FileIcon>
        {getFileIcon(file)}
      </Styled.FileIcon>
      <Styled.FileName>
        {file.filename}
      </Styled.FileName>
    </Styled.FileContainer>
  )
}

const FileAttachments = ({ files }:any) => {
  const [showAll, setShowAll] = useState(false);
  const [showText, setShowText] = useState(false);
  const [displayItems, setDisplayItems] = useState<Array<any>>([]);
  const screenSize:string = useBreakpoint();

  const toggleShowAll = (e:any) => {
    e.stopPropagation();
    setShowAll(!showAll);
  }

  useEffect(() => {
    if (!files || !files.length) {
      return;
    }
    const noItems = filesPerScreenSize[screenSize] || 8;
    setDisplayItems(showAll ? [...files] : [...files].slice(0, noItems));
    setShowText(files.length > noItems);
  }, [screenSize, files, showAll]);

  if (!files || !files.length) {
    return null;
  }

  return (
    <>
      <Styled.Container>
        {displayItems.map((file: any) =>
          <Attachment file={file} key={file.id || file.imgId || file.filename} />
        )}
      </Styled.Container>
      {showText && (
        <Styled.ShowAll onClick={toggleShowAll}>
          {showAll ? 'Hide' : 'See all'} attachments
        </Styled.ShowAll>
      )}
    </>
  )
}

export default FileAttachments;